import sum from "lodash/sum";
import moment from "moment-timezone";
import numeral from "numeral";
import { ConfidenceLevel, KeyResultType, KeyResultBinaryChoice } from "@teaming/common-enums";

const binaryChoiceMap = {
	0: KeyResultBinaryChoice.INCOMPLETE,
	[ KeyResultBinaryChoice.INCOMPLETE ]: KeyResultBinaryChoice.INCOMPLETE,
	1: KeyResultBinaryChoice.COMPLETE,
	[ KeyResultBinaryChoice.COMPLETE ]: KeyResultBinaryChoice.COMPLETE,
};

const _calculateNumericKRPercentCompleted = keyResult => {
	const { currentValue, goalValue, } = keyResult;
	const currentNumber = numeral( currentValue ).value();
	const goalNumber = numeral( goalValue ).value();
	return Math.floor( ( currentNumber / goalNumber ) * 100 );
};

const completionCalculations = {
	[ KeyResultType.BINARY ]: keyResult => {
		const { currentValue, goalValue, } = keyResult;
		return currentValue === goalValue ? 100 : 0;
	},
	[ KeyResultType.PERCENTAGE ]: _calculateNumericKRPercentCompleted,
	[ KeyResultType.CURRENCY ]: _calculateNumericKRPercentCompleted,
	[ KeyResultType.NUMERIC ]: _calculateNumericKRPercentCompleted,
};

/**
 * Calculate the completion percentage of a given key result (based on type).
 * @param {Object} keyResult
 * @returns {Number}
 */
const calculateKeyResultPercentCompleted = keyResult => {
	const calculator = completionCalculations[ keyResult.type ] || ( () => 0 );
	return calculator( keyResult );
};

/**
 * Sums the KR completion percentages for a given goal, and distills it
 * to a single percentage number. Used to display HOW MUCH of a goal
 * has been completed.
 * @param {Goal} goal
 * @param {Array.<KeyResult>} keyResults = []
 * @returns {Number}
 */
const calculatePercentCompleted = ( goal, keyResults = [] ) => {
	// if we've loaded key results, calculate completion percentage
	// based on those
	if ( keyResults.length > 0 ) {
		const completions = keyResults.map( keyResult => {
			return Math.min( 100, calculateKeyResultPercentCompleted( keyResult ) );
		} );
		const totalCompletion = sum( completions );
		return Math.floor( totalCompletion / completions.length );
	}

	// if we haven't yet loaded key results, calculate completion based
	// on goal metadata, if possible
	return goal.keyResultSummary?.progress || 0;
};

/**
 * Calculates the amount of time that has passed for a given goal and
 * distills it to a percentage. Used to display HOW LONG a given goal
 * has been in progress.
 * @param {Goal} goal
 * @param {String} timezone
 * @returns {Number}
 */
const calculatePercentProgressed = goal => {
	// TODO: convert to Luxon
	const now = moment();
	const start = goal.plannedStartDate;
	const finish = goal.plannedFinishDate;
	// eslint-disable-next-line no-magic-numbers
	const nowDiff = finish.diff( now, "days" );
	if ( nowDiff <= 0 ) {
		return 100;
	}
	const plannedDiff = finish.diff( start, "days" );
	return Math.floor( Math.max( 0, plannedDiff - nowDiff ) / plannedDiff * 100 );
};

const NO_GOAL = {
	percentProgressed: 0,
};

export const extendKeyResult = ( keyResult, goal = NO_GOAL ) => {
	keyResult.percentCompleted = calculateKeyResultPercentCompleted( keyResult );
	keyResult.percentProgressed = goal.percentProgressed;
	if ( keyResult.type === KeyResultType.BINARY ) {
		// FIXME: apparently the API sends the binary values as both enums AND numbers, depending on
		// which endpoint is called.
		//
		// This is sad and makes me cry. Have added a card to address this.
		keyResult.currentValue = binaryChoiceMap[ keyResult.currentValue ];
		keyResult.goalValue = binaryChoiceMap[ keyResult.goalValue ];
	} else {
		keyResult.currentValue = numeral( keyResult.currentValue );
		keyResult.goalValue = numeral( keyResult.goalValue );
	}
};

/**
 * Takes a goal just returned from an API call and sets dates
 * to actual date instances, calculates percent completed/progressed,
 * applies proper enum values, etc.
 * @param {Goal} goal
 * @returns {Goal}
 */
export const extendGoal = goal => {
	// TODO: convert to Luxon
	goal.created = moment( goal.created );
	// TODO: convert to Luxon
	goal.lastUpdated = moment( goal.lastUpdated );
	// TODO: convert to Luxon
	goal.plannedStartDate = moment( goal.plannedStartDate );
	// TODO: convert to Luxon
	goal.plannedFinishDate = moment( goal.plannedFinishDate );
	goal.confidence = ConfidenceLevel.keyFor( goal.confidence );
	goal.percentCompleted = calculatePercentCompleted( goal );
	goal.percentProgressed = calculatePercentProgressed( goal );

	goal.keyResults.forEach( keyResult => {
		extendKeyResult( keyResult, goal );
	} );

	return goal;
};
